import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {NavigationType, useLocation, useNavigationType} from "react-router-dom";
import {recallScroll, rememberScroll} from "../../navigation/scrollMemory";
import {buildTocTree, type TocEntry} from "./tocUtils";
import {findAnchorTarget} from "./anchorTarget";
import type {DibDocument} from "./types";

const collectTocIds = (entries: TocEntry[]): string[] =>
    entries.flatMap((e) => [e.id, ...collectTocIds(e.children)]);

/** Section a document load lands on: the host's when embedded, else the
 *  page's URL hash, read at call time as before. */
const landingTarget = (isEmbedded: boolean, sectionId: string | undefined) =>
    isEmbedded ? (sectionId ?? "") : window.location.hash.slice(1);

/** TOC entry a landing holds: the landing section's own entry when it has
 *  one, else the URL's segment — none for a whole topic. */
const landingEntry = (
    tocIds: Set<string>,
    target: string,
    segmentId: string | undefined
) => (target && tocIds.has(target) ? target : (segmentId ?? null));

/** The scroll-spy's pick: the topmost of the innermost in-band sections.
 *  Sections nest, so a wrapping ancestor is in band whenever a descendant
 *  is and would always win a topmost sort — only innermost members
 *  compete. Rects are measured now: observer entry rects are stale for
 *  members that didn't change. */
const bandWinner = (inBand: Set<HTMLElement>) => {
    const members = Array.from(inBand);
    return members
        .filter(
            (s) => !members.some((other) => other !== s && s.contains(other))
        )
        .sort(
            (a, b) =>
                a.getBoundingClientRect().top - b.getBoundingClientRect().top
        )[0];
};

/** Scrolls `box` so `el` sits at its top or centre. Only `box` moves:
 *  scrollIntoView would scroll every scrollable ancestor as well, and an
 *  embedded article's ancestors belong to the host page. */
const scrollWithin = (
    box: HTMLElement,
    el: HTMLElement,
    block: "start" | "center",
    behavior: ScrollBehavior = "auto"
) => {
    const boxRect = box.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();
    const inset = block === "center" ? (boxRect.height - elRect.height) / 2 : 0;
    box.scrollTo({
        top: box.scrollTop + elRect.top - boxRect.top - inset,
        behavior,
    });
};

/**
 * Owns everything that keeps the TOC pane and the article in sync for the
 * HTML resource view: the active TOC entry (highlight + expansion), the
 * landing of each document load, the initial TOC-pane scroll to the active
 * entry, and TOC-click navigation. The consumer attaches the returned refs
 * to the article and TOC nav elements and forwards activeId/handleTocClick.
 *
 * The active entry is either held or tracked:
 * - Held: an explicit target — a document load's landing entry, or a
 *   clicked TOC entry — with tracking suppressed until the reader takes
 *   over: their first input in the article, or the first scroll we didn't
 *   cause. Releasing recomputes nothing by itself; the highlight moves on
 *   the next scroll or band change, so a click in the article changes
 *   nothing until the reader scrolls.
 * - Tracked: derived from the reader's position — the article's first TOC
 *   section at its very top, the scroll-spy's band winner anywhere else.
 * A landing holds the URL hash's entry when it has one, else the segment's,
 * else none: a plain topic shows no highlight until the reader scrolls, a
 * segment shows its own entry. Returning to a history entry instead
 * restores its reading position and tracks from there.
 *
 * With `embedded`, the article is a guest in a host page (the chat work
 * island) that owns the URL, the history and every scroll box around the
 * article. The landing section is the host's `sectionId` instead of the URL
 * hash; nothing is remembered, restored or written to the URL, and host
 * navigations don't re-land the article. Targets are looked up inside the
 * article only (the host page can carry the same ids), and only the article
 * and the TOC pane scroll: box-local scrollTo instead of scrollIntoView.
 */
export const useTocScrollSync = (
    doc: DibDocument | undefined,
    resourceId: string,
    segmentId: string | undefined,
    embedded?: {sectionId?: string}
) => {
    const isEmbedded = embedded !== undefined;
    const landingSectionId = embedded?.sectionId;
    const articleRef = useRef<HTMLElement>(null);
    const tocNavRef = useRef<HTMLElement>(null);
    const isTrackingSuppressed = useRef(false);
    const releaseOnNextScroll = useRef(false);
    const pendingTocScrollId = useRef<string | null>(null);

    // Set of section IDs that appear in the TOC. The IntersectionObserver
    // observes only these so non-TOC sections in the article body can't
    // hijack the active highlight.
    const tocIds = useMemo(() => {
        if (!doc) return new Set<string>();
        return new Set(collectTocIds(buildTocTree(doc)));
    }, [doc]);

    const [activeId, setActiveId] = useState<string | null>(() =>
        landingEntry(
            tocIds,
            landingTarget(isEmbedded, landingSectionId),
            segmentId
        )
    );
    const location = useLocation();
    const navigationType = useNavigationType();
    // The history entry reading positions are remembered against. None when
    // embedded: the host page's history is not this article's, and host
    // navigations must not re-run the landing effect.
    const historyKey = isEmbedded ? null : location.key;
    const historyPath = isEmbedded ? null : location.pathname;
    const historyNavigation = isEmbedded ? null : navigationType;

    // Record the article's scroll position per history entry so a return to
    // the entry — back/forward navigation or a reload — can restore the
    // reading position (see the landing effect below).
    useEffect(() => {
        const root = articleRef.current;
        if (!root || historyKey === null || historyPath === null) return;
        const save = () =>
            rememberScroll(historyKey, historyPath, root.scrollTop);
        root.addEventListener("scroll", save, {passive: true});
        return () => root.removeEventListener("scroll", save);
    }, [historyKey, historyPath]);

    // A hold suppresses tracking until the reader's first interaction. It
    // is armed — the next article scroll releases it — unless we are about
    // to scroll the article ourselves: then it stays disarmed until that
    // scroll comes to rest (scrollend arms it), so our own scroll events
    // can never release it. Releasing on a timer instead would let a long
    // smooth scroll cascade the highlight through every section it passes.
    // Both helpers set the arm explicitly, so a stale arm can't leak across
    // holds.
    const suppressTracking = useCallback(
        ({releaseOnNextScroll: armed = false} = {}) => {
            isTrackingSuppressed.current = true;
            releaseOnNextScroll.current = armed;
        },
        []
    );
    const resumeTracking = useCallback(() => {
        isTrackingSuppressed.current = false;
        releaseOnNextScroll.current = false;
    }, []);

    // Land each document: hold its landing entry, then position the
    // article. Also runs while the document is still loading, so the
    // kept-visible TOC marks an incoming segment at once (a seg=1 entry is a
    // <Link> that never calls handleTocClick). The hold is armed — nothing
    // of ours scrolls — unless the landing jumps below; it is in place
    // before the scroll-spy observer's initial callback can arrive, so that
    // callback can't replace the landing entry with whatever sits in the
    // band at the top. Once loaded, the TOC pane scrolls to the landing
    // entry when it is the committed active entry (next effect): TOC
    // entries only exist in the DOM once their ancestor chain is expanded,
    // and expansion derives from activeId.
    useEffect(() => {
        const target = landingTarget(isEmbedded, landingSectionId);
        const entry = landingEntry(tocIds, target, segmentId);
        setActiveId(entry);
        suppressTracking({releaseOnNextScroll: true});
        if (!doc) return;
        if (entry) pendingTocScrollId.current = entry;
        if (!doc.access || doc.root.length === 0) return;
        // Returning to this history entry (back/forward, or a reload — both
        // are POPs): restore the recorded reading position instead of
        // jumping, and track so the highlight follows wherever the restore
        // lands.
        const restoredTop =
            historyNavigation === NavigationType.Pop &&
            historyKey !== null &&
            historyPath !== null
                ? recallScroll(historyKey, historyPath)
                : undefined;
        if (restoredTop !== undefined) {
            resumeTracking();
            requestAnimationFrame(() => {
                const root = articleRef.current;
                if (root) root.scrollTop = restoredTop;
            });
            return;
        }
        if (!target) return;
        // Jump to the landing target. The re-assert inside the rAF clears an
        // arm set by a scrollend that slipped in between; when the target
        // doesn't exist nothing scrolls, so the hold stays armed.
        suppressTracking();
        requestAnimationFrame(() => {
            // Exact id first; if none, a unique case-insensitive match inside
            // the article (related-source anchors arrive as "P3-5" for "p3-5").
            // Embedded, the exact lookup stays inside the article too.
            const root = articleRef.current;
            const el = isEmbedded
                ? root && findAnchorTarget(target, root)
                : findAnchorTarget(target, document, root);
            if (!el) {
                suppressTracking({releaseOnNextScroll: true});
                return;
            }
            suppressTracking();
            if (isEmbedded && root) scrollWithin(root, el, "start");
            else el.scrollIntoView({block: "start"});
        });
    }, [
        doc,
        tocIds,
        resourceId,
        segmentId,
        historyNavigation,
        historyKey,
        historyPath,
        isEmbedded,
        landingSectionId,
        suppressTracking,
        resumeTracking,
    ]);

    // Consume the pending TOC scroll once the target is the rendered active
    // entry: same effect pass when setActiveId bailed (already active), or
    // the follow-up commit when it re-rendered — `doc` is deliberately a dep
    // for the former even though nothing here reads it. Entries already fully
    // inside the nav viewport are left alone so short TOCs, top-of-document
    // loads, and TOC-click navigations don't jump.
    useEffect(() => {
        const target = pendingTocScrollId.current;
        if (!target || activeId !== target) return;
        pendingTocScrollId.current = null;
        const nav = tocNavRef.current;
        const link = nav?.querySelector<HTMLAnchorElement>(
            'a[aria-current="location"]'
        );
        if (!nav || !link) return;
        const navRect = nav.getBoundingClientRect();
        const linkRect = link.getBoundingClientRect();
        if (linkRect.top >= navRect.top && linkRect.bottom <= navRect.bottom)
            return;
        if (isEmbedded) scrollWithin(nav, link, "center");
        else link.scrollIntoView({block: "center"});
    }, [activeId, doc, isEmbedded]);

    // Release a hold on the reader's first direct input in the article: a
    // programmatic scroll produces none of these, so they are unambiguous
    // user signals — capture phase so a stopPropagation inside rendered
    // content can't swallow them. Input the DOM cannot observe directly
    // (scrollbar drags in Firefox, find-in-page) releases through the first
    // scroll of an armed hold (the tracking effect's scroll listener);
    // scrollend arms a hold taken for one of our own scrolls once that
    // scroll has come to rest. Input listeners are article-scoped on
    // purpose — a pointerdown on the TOC pane must NOT release, or a second
    // TOC click mid-flight would let a spy callback flash a wrong highlight
    // in the gap between pointerdown and handleTocClick re-suppressing.
    // scrollend doesn't bubble, so the plain listener sees only the
    // article's own scrolling. Attached once: the consumer renders the
    // article element unconditionally.
    useEffect(() => {
        const root = articleRef.current;
        if (!root) return;
        const arm = () => {
            if (isTrackingSuppressed.current)
                releaseOnNextScroll.current = true;
        };
        const capture = {capture: true, passive: true} as const;
        root.addEventListener("wheel", resumeTracking, capture);
        root.addEventListener("touchstart", resumeTracking, capture);
        root.addEventListener("pointerdown", resumeTracking, capture);
        root.addEventListener("keydown", resumeTracking, capture);
        root.addEventListener("scrollend", arm);
        return () => {
            root.removeEventListener("wheel", resumeTracking, capture);
            root.removeEventListener("touchstart", resumeTracking, capture);
            root.removeEventListener("pointerdown", resumeTracking, capture);
            root.removeEventListener("keydown", resumeTracking, capture);
            root.removeEventListener("scrollend", arm);
        };
    }, [resumeTracking]);

    // Tracking: `track` derives the active entry from the reader's position
    // and does nothing while the highlight is held. At the article's very
    // top it is the first TOC section the article renders (the reading band
    // can miss short top-of-document sections); anywhere else, the topmost
    // innermost TOC section in the -10%/-70% band. Observer entries only
    // report changes, so the in-band set is kept here — also while held, so
    // the picture is current when tracking resumes. Every observer callback
    // and every article scroll tracks, so both apply the same rule. A scroll
    // that finds an armed hold is the reader's: it releases the hold first,
    // so the scroll that hands the highlight back also places it.
    useEffect(() => {
        const root = articleRef.current;
        if (!root || !doc) return;

        // Document order, so sections[0] is the topmost. In a segmented
        // topic the article renders only the current segment's sections, so
        // it need not be the TOC tree's first entry.
        const sections = Array.from(
            root.querySelectorAll<HTMLElement>("section[id]")
        ).filter((s) => tocIds.has(s.id));

        const inBand = new Set<HTMLElement>();

        const track = () => {
            if (isTrackingSuppressed.current) return;
            const winner =
                root.scrollTop <= 0 ? sections[0] : bandWinner(inBand);
            if (winner) setActiveId(winner.id);
        };

        const observer = new IntersectionObserver(
            (entries) => {
                for (const entry of entries) {
                    if (entry.isIntersecting) {
                        inBand.add(entry.target as HTMLElement);
                    } else {
                        inBand.delete(entry.target as HTMLElement);
                    }
                }
                track();
            },
            {root, rootMargin: "-10% 0px -70% 0px", threshold: 0}
        );

        const onScroll = () => {
            if (releaseOnNextScroll.current) resumeTracking();
            track();
        };

        sections.forEach((s) => observer.observe(s));
        root.addEventListener("scroll", onScroll, {passive: true});
        return () => {
            observer.disconnect();
            root.removeEventListener("scroll", onScroll);
        };
    }, [doc, tocIds, resumeTracking]);

    const handleTocClick = (id: string) => {
        suppressTracking();
        setActiveId(id);
        if (isEmbedded) {
            // The host owns the URL: no hash write, and only the article
            // box scrolls, to its own element of that id.
            const root = articleRef.current;
            const el = root?.querySelector<HTMLElement>(`#${CSS.escape(id)}`);
            if (root && el) scrollWithin(root, el, "start", "smooth");
            return;
        }
        // Keep the existing state object: React Router stores {idx, key}
        // there, and both back navigation and scroll memory depend on it.
        window.history.replaceState(window.history.state, "", `#${id}`);
        document
            .getElementById(id)
            ?.scrollIntoView({behavior: "smooth", block: "start"});
    };

    return {articleRef, tocNavRef, activeId, handleTocClick};
};
